import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supaFetch } from '../lib/supabase.js';
import { queryKeys } from '../lib/queryKeys.js';

// ─── RETAILER CONTACTS ────────────────────────────────────────────────────────
// People at a customer account. An account (retailer_applications) has many
// contacts; at most one is the active primary. When someone leaves the customer
// company you deactivate them and promote a successor — history is kept and all
// future comms follow the new primary, instead of editing over the departed
// person's details.

// Optional fields are stored as NULL, never '' — the seed migration and any
// coalesce()-based comms fallback rely on that convention.
const orNull = (v) => {
  const s = (v ?? '').trim();
  return s === '' ? null : s;
};

export function useRetailerContacts(accountId) {
  return useQuery({
    queryKey: queryKeys.admin.retailerContacts(accountId),
    enabled: !!accountId,
    queryFn: () => supaFetch('GET', 'retailer_contacts', null,
      `?retailer_application_id=eq.${encodeURIComponent(accountId)}&order=active.desc,is_primary.desc,created_at.asc`),
    select: (result) => result.data ?? [],
  });
}

export function useSaveRetailerContact(accountId) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (contact) => {
      const { id, name, job_title, email, phone, whatsapp, is_primary } = contact;
      const row = {
        name: name.trim(),
        job_title: orNull(job_title),
        email: orNull(email),
        phone: orNull(phone),
        whatsapp: orNull(whatsapp),
        retailer_application_id: accountId,
      };
      // Edits PATCH only the editable fields — never active/ended_at/is_primary,
      // so a stale form can't resurrect a departed contact or demote a primary.
      if (id) return supaFetch('PATCH', 'retailer_contacts', row, `?id=eq.${encodeURIComponent(id)}`, { Prefer: 'return=minimal' });
      return supaFetch('POST', 'retailer_contacts', { ...row, is_primary: !!is_primary }, '', { Prefer: 'return=minimal' });
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: queryKeys.admin.retailerContacts(accountId) }),
  });
}

// Promote a contact to primary — one atomic RPC (demote incumbent + promote
// target in a single transaction). Two client-side PATCHes previously opened a
// zero-primary window whenever the second call failed.
export function usePromoteRetailerContact(accountId) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (contactId) => supaFetch('POST', 'rpc/promote_retailer_contact',
      { p_contact_id: contactId }, '', { Prefer: 'return=minimal' }),
    onSettled: () => queryClient.invalidateQueries({ queryKey: queryKeys.admin.retailerContacts(accountId) }),
  });
}

// Mark a contact as having left the customer company. is_primary is kept as a
// historical marker of who WAS primary — the partial unique index only counts
// ACTIVE primaries and the UI badge is gated on `active`, so keeping it costs
// nothing and preserves the record.
export function useDeactivateRetailerContact(accountId) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (contactId) => supaFetch('PATCH', 'retailer_contacts',
      { active: false, ended_at: new Date().toISOString() },
      `?id=eq.${encodeURIComponent(contactId)}`, { Prefer: 'return=minimal' }),
    onSettled: () => queryClient.invalidateQueries({ queryKey: queryKeys.admin.retailerContacts(accountId) }),
  });
}
